import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";
import { StreamVideo } from "../StreamVideo";
import { DevicePermissions } from "../DevicePermissions";

const socket = io(process.env.REACT_APP_API_URL, {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
});

const RTC_CONFIG = {
  iceServers: [
    {
      urls: [
        "stun:stun.l.google.com:19302",
        "stun:global.stun.twilio.com:3478",
      ],
    },
    {
      urls: [
        "turn:stun.evan-brass.net",
        "turn:stun.evan-brass.net?transport=tcp",
        "turn:stun.evan-brass.net?transport=udp",
        "stun:stun.evan-brass.net",
      ],
      username: "guest",
      credential: "password",
    },
    {
      urls: [
        "turn:turn.pickitforme.com",
        "turn:turn.pickitforme.com?transport=tcp",
        "turn:turn.pickitforme.com?transport=udp",
        "stun:stun.pickitforme.com",
      ],
      username: "user1",
      credential: "password1",
    },
  ],
};

export const useCallSocket = ({ user, videoOff, mute }) => {
  const connectionRef = useRef(null);
  const activeStreamRef = useRef(null);
  const remoteVideo = useRef(null);
  const Permissions = useRef(null);

  const [incomingCall, setIncomingCall] = useState(null);
  const [activeUsers, setActiveUsers] = useState([]);

  // ---------------- Permissions ----------------
  useEffect(() => {
    Permissions.current = DevicePermissions(navigator.permissions);
  }, []);

  // ---------------- Register Socket ----------------
  useEffect(() => {
    if (!user) return;

    const handleActiveUsers = ({ users }) => {
      // Force new array reference to trigger rerenders
      setActiveUsers([...users.filter(id => id !== user._id)]);
    };

     const handleDisconnect = () => {
      console.log("🔌 socket disconnected — cleaning up");
      cleanupCall();
    };

    socket.on("disconnect", handleDisconnect);

    const handleIncomingCall = ({ from, name, signal }) => {
      console.log("📞 Incoming call from", name);
      setIncomingCall({ from, name, signal });
    };

    const cleanupCall = () => {
      if (connectionRef.current) {
        connectionRef.current.destroy();
        connectionRef.current = null;
      }

      stopMediaTracks();        // ✅ THIS turns camera OFF
      setIncomingCall(null);
      StreamVideo({ on: false });
    };

    // Join room
    socket.emit("join-room", {
      userId: user._id,
      userName: user.name,
      socketId: socket.id,
    });

    // Request initial active users
    socket.emit("get-active-users");

    // Listen for events
    socket.on("active-users", handleActiveUsers);
    socket.on("calluser", handleIncomingCall);
    socket.on("callended", cleanupCall);
    socket.on("userleft", cleanupCall);
    socket.on("user-unavailable", cleanupCall);

    return () => {
      socket.off("active-users", handleActiveUsers);
      socket.off("calluser", handleIncomingCall);
      socket.off("callended", cleanupCall);
      socket.off("userleft", cleanupCall);
      socket.off("user-unavailable", cleanupCall);
      socket.off("disconnect", handleDisconnect);
      stopMediaTracks();
    };
  }, [user]);

  const stopMediaTracks = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      activeStreamRef.current = null;
    }

    if (remoteVideo.current) {
      remoteVideo.current.srcObject = null;
    }
  };

  // ---------------- Accept Call ----------------
  const acceptCall = async () => {
    if (!incomingCall) return;

    activeStreamRef.current = await navigator.mediaDevices.getUserMedia({
      video: !videoOff,
      audio: !mute,
    });

    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream: activeStreamRef.current,
      config: RTC_CONFIG,
    });

    peer.on("signal", signal => {
      socket.emit("answercall", {
        to: incomingCall.from,
        signal,
      });
    });

    peer.on("close", stopCall);
    peer.on("error", console.error);

    peer.signal(incomingCall.signal);
    connectionRef.current = peer;
    setIncomingCall(null);
  };

  // ---------------- Call User ----------------
  const callUser = socketId => {
    const peer = new Peer({
      initiator: true,
      trickle: false,
      config: RTC_CONFIG,
      offerOptions: {
        offerToReceiveVideo: true,
        offerToReceiveAudio: true,
      },
    });

    peer.on("signal", signal => {
      socket.emit("calluser", {
        userToCall: socketId,
        signalData: signal,
        from: socket.id,
        name: user.name,
      });
    });

    peer.on("stream", stream => {
      console.log("🎥 Remote stream received");
      if (remoteVideo.current) {
        remoteVideo.current.srcObject = stream;
      }
    });

    socket.once("callaccepted", signal => {
      if (!peer.destroyed) peer.signal(signal);
    });

    peer.on("close", stopCall);
    peer.on("error", console.error);

    connectionRef.current = peer;
  };

  const stopCall = () => {
      if (connectionRef.current) {
        connectionRef.current.destroy();
        connectionRef.current = null;
      }

      stopMediaTracks();        // ✅ THIS turns camera OFF
      setIncomingCall(null);
      StreamVideo({ on: false });
    };

  return {
    remoteVideo,
    callUser,
    stopCall,
    incomingCall,
    acceptCall,
    activeUsers, // reactive now
  };
};
